import { useMutation, useQuery } from "@tanstack/react-query"
import { createFeedCommentHiddenMemoAdmin, createFeedHiddenMemoAdmin, createOrUpdateFeedCommentHiddenAdmin, createOrUpdateFeedHiddenAdmin, getAllFeedCommentHiddenMemoAdminByFeedCommentHiddenId, getAllFeedHiddenMemoAdminByFeedHiddenId, getFeedAndCommentClaimAdminByTargetClaimIdAndTargetType, getFeedAndCommentTotalClaimCntAdminByTargetIdAndTargetType } from "../../../../../service/feedAndCommentClaim";
import { App, Select, TableColumnsType } from "antd";
import { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import { useState } from "react";

const useGetFeedAndCommentClaimAdminByTargetClaimIdAndTargetType = <T,>(targetClaimId: number, targetType: string, responseQuery?: string) => {
    const query = useQuery({
        queryKey: ['getFeedAndCommentClaimAdminByTargetClaimIdAndTargetType', targetClaimId, targetType, responseQuery],
        queryFn: () => getFeedAndCommentClaimAdminByTargetClaimIdAndTargetType<T>(targetClaimId, targetType, responseQuery),
        select: res => res.data.data.getFeedAndCommentClaimAdminByTargetClaimIdAndTargetType,
        enabled: !!targetClaimId && !!targetType
    })
    return query;
}

const useGetFeedAndCommentTotalClaimCntAdminByTargetIdAndTargetType = (targetClaimId?: number, targetType?: string) => {
    const query = useQuery({
        queryKey: ['getFeedAndCommentTotalClaimCntAdminByTargetIdAndTargetType', targetClaimId, targetType],
        queryFn: () => getFeedAndCommentTotalClaimCntAdminByTargetIdAndTargetType(targetClaimId ?? 0, targetType ?? 'feed'),
        select: res => res.data,
        enabled: !!targetClaimId && !!targetType
    })
    return query;
}

const useGetAllFeedOrCommentHiddenMemoAdminByHiddenIdAndTargetType = (hiddenId?: number, targetType?: string) => {
    const isFeed = targetType === 'feed';

    const queryKey = isFeed
        ? ['getAllFeedHiddenMemoAdminByFeedHiddenId', hiddenId]
        : ['getAllFeedCommentHiddenMemoAdminByFeedCommentHiddenId', hiddenId];

    const queryFn = () => {
        if (!hiddenId) return Promise.reject(new Error('hiddenId is undefined'));
        return isFeed
            ? getAllFeedHiddenMemoAdminByFeedHiddenId(hiddenId)
            : getAllFeedCommentHiddenMemoAdminByFeedCommentHiddenId(hiddenId);
    };

    const query = useQuery({
        queryKey,
        queryFn,
        select: res => res.data,
        enabled: !!hiddenId && !!targetType,
    });
    return query;
}

const useCreateFeedAndCommentHiddenMemoAdmin = (targetType?: string) => {
    const isFeed = targetType === 'feed';
    const mutationFn = isFeed ? createFeedHiddenMemoAdmin : createFeedCommentHiddenMemoAdmin;
    const mutation = useMutation({ mutationFn });
    return mutation
}

const useCreateOrUpdateFeedHiddenAdmin = () => {
    const mutation = useMutation({ mutationFn: createOrUpdateFeedHiddenAdmin })
    return mutation
}

const useCreateOrUpdateFeedCommentHiddenAdmin = () => {
    const mutation = useMutation({ mutationFn: createOrUpdateFeedCommentHiddenAdmin })
    return mutation
}

const HiddenStatusSelect = ({ targetId, claimId, targetType, status, reason, isOnfinish = true, onChange }: { targetId: number, claimId?: number, targetType: string, status: string, reason?: string, isOnfinish?: boolean, onChange?: (value: string) => void }) => {
    const [selectValue, setSelectValue] = useState<string>(status)
    const createOrUpdateFeedHiddenAdmin = useCreateOrUpdateFeedHiddenAdmin();
    const createOrUpdateFeedCommentHiddenAdmin = useCreateOrUpdateFeedCommentHiddenAdmin();
    const { message } = App.useApp();

    const onFinish = (targetType: string, status: string) => {
        if (!isOnfinish) return;
        if (targetType === 'feed') {
            const createOrUpdateFeedHiddenAdminRequest: ICreateOrUpdateFeedHiddenAdminRequest = {
                feedId: targetId,
                feedClaimId: claimId,
                status: status === 'null' ? null : status,
                reason: reason
            }
            createOrUpdateFeedHiddenAdmin.mutate(createOrUpdateFeedHiddenAdminRequest, {
                onSuccess: () => { message.success('상태 변경 성공!') }
            })
        } else {
            const createOrUpdateFeedCommentHiddenAdminRequest: ICreateOrUpdateFeedCommentHiddenAdminRequest = {
                feedCommentId: targetId,
                feedCommentClaimId: claimId,
                status: status === 'null' ? null : status,
                reason: reason
            }

            createOrUpdateFeedCommentHiddenAdmin.mutate(createOrUpdateFeedCommentHiddenAdminRequest, {
                onSuccess: () => { message.success('상태 변경 성공!') },
            })
        }
    }

    const selectOnChange = (value: string, option?: DefaultOptionType | DefaultOptionType[] | undefined) => {
        onChange?.(value)
        setSelectValue(value)
        onFinish(targetType, value)
    }

    return <>
        <Select loading={createOrUpdateFeedHiddenAdmin.isPending || createOrUpdateFeedCommentHiddenAdmin.isPending} defaultValue={selectValue} onChange={selectOnChange}>
            <Select.Option value='null'>미처리</Select.Option>
            <Select.Option value='complete'>처리완료(노출유지)</Select.Option>
            <Select.Option value='hidden'>숨김처리</Select.Option>
        </Select>
    </>
}

const feedAndCommentHiddenMemoColumns: TableColumnsType<IFeedAndCommentHiddenMemo> = [{ title: 'No', dataIndex: 'id' }, { title: '내용', dataIndex: 'content' }, { title: '작성자', dataIndex: 'memberName' }, { title: '작성일시', dataIndex: 'createdDate', render: q => dayjs(q).format('YYYY.MM.DD HH:mm'), sorter: (a, b) => dayjs(b.createdDate).diff(dayjs(a.createdDate)) }]


export { useGetFeedAndCommentClaimAdminByTargetClaimIdAndTargetType, useGetFeedAndCommentTotalClaimCntAdminByTargetIdAndTargetType, useGetAllFeedOrCommentHiddenMemoAdminByHiddenIdAndTargetType, HiddenStatusSelect, feedAndCommentHiddenMemoColumns, useCreateFeedAndCommentHiddenMemoAdmin, useCreateOrUpdateFeedHiddenAdmin, useCreateOrUpdateFeedCommentHiddenAdmin }